import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  setDoc,
  query,
  where,
  increment,
  writeBatch,
} from "firebase/firestore";

import { db, auth } from "../firebase/config";
import { storage } from "../firebase/config";
import { ref, deleteObject } from "firebase/storage";

// ============================================================
// GET MEMBER COUNT
// ============================================================

export const getMemberCount = async () => {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    throw new Error("User not authenticated");
  }

  const snapshot = await getDocs(collection(db, "gyms", uid, "members"));

  return snapshot.size;
};

// ============================================================
// MEMBER COLLECTION
// ============================================================

const getMemberCollection = () => {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    throw new Error("User not authenticated");
  }

  return collection(db, "gyms", uid, "members");
};

// ============================================================
// ADD MEMBER
// ============================================================

export const addMember = async (data) => {
  // IMPORTANT:
  // data.memberId already contains:
  // MEM-000001
  // MEM-000002
  // MEM-000003
  // etc.

  console.log("ADDING MEMBER:", data);
  console.log("MEMBER ID BEING SAVED:", data.memberId);

  // Create member
  const memberRef = await addDoc(getMemberCollection(), data);

  // ==========================================================
  // UPDATE STORAGE USAGE
  // ==========================================================

  if (data.imageSize > 0) {
    await updateDoc(doc(db, "gyms", auth.currentUser.uid), {
      storageUsed: increment(Number(data.imageSize)),
    });
  }

  // ==========================================================
  // CREATE MEMBER PORTAL
  // ==========================================================

  const portalRef = doc(collection(db, "memberPortal"));

  await setDoc(portalRef, {
    gymId: auth.currentUser.uid,

    // Firestore document ID
    memberId: memberRef.id,

    createdAt: new Date().toISOString(),
  });

  // ==========================================================
  // SAVE PORTAL TOKEN
  // ==========================================================

  // IMPORTANT:
  // Only update accessToken.
  // DO NOT change memberId here.

  await updateDoc(memberRef, {
    accessToken: portalRef.id,
  });

  console.log("MEMBER SAVED:", memberRef.id);
  console.log("CUSTOM MEMBER ID:", data.memberId);

  return memberRef.id;
};

// ============================================================
// GET MEMBERS
// ============================================================

export const getMembers = async () => {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    throw new Error("User not authenticated");
  }

  const membersRef = collection(db, "gyms", uid, "members");

  const snapshot = await getDocs(membersRef);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const batch = writeBatch(db);

  const members = snapshot.docs.map((memberDoc) => {
    const data = memberDoc.data();

    let status = data.status || "Active";

    // Check Firebase expiryDate
    if (data.expiryDate) {
      const expiryDate = new Date(data.expiryDate);
      expiryDate.setHours(0, 0, 0, 0);

      // Member is expired AFTER expiry date
      if (expiryDate < today) {
        status = "Expired";
      }
    }

    // If status changed, update Firebase
    if (data.status !== status) {
      batch.update(memberDoc.ref, {
        status: status,
      });
    }

    return {
      id: memberDoc.id,
      ...data,

      // Use Firebase-calculated status
      status,
    };
  });

  // Save all changed statuses to Firebase
  await batch.commit();

  return members;
};

// ============================================================
// DELETE MEMBER
// ============================================================

export const deleteMember = async (id) => {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    throw new Error("User not authenticated");
  }

  // Member document
  const memberRef = doc(db, "gyms", uid, "members", id);

  const memberSnap = await getDoc(memberRef);

  if (!memberSnap.exists()) {
    throw new Error("Member not found");
  }

  const member = memberSnap.data();

  // ==========================================================
  // DELETE PHOTO
  // ==========================================================

  if (member.photoPath) {
    try {
      await deleteObject(ref(storage, member.photoPath));

      console.log("Photo deleted successfully");
    } catch (error) {
      console.error("Delete Photo Error:", error);
      console.error("Error Code:", error.code);
      console.error("Error Message:", error.message);

      throw error;
    }
  }

  // ==========================================================
  // DECREASE STORAGE
  // ==========================================================

  if (member.imageSize > 0) {
    await updateDoc(doc(db, "gyms", uid), {
      storageUsed: increment(-Number(member.imageSize)),
    });
  }

  // ==========================================================
  // DELETE MEMBER
  // ==========================================================

  await deleteDoc(memberRef);

  // ==========================================================
  // DELETE PORTAL
  // ==========================================================

  if (member.accessToken) {
    try {
      await deleteDoc(doc(db, "memberPortal", member.accessToken));
    } catch (error) {
      console.log("Portal deletion error:", error);
    }
  }
};

// ============================================================
// GET MEMBER BY ID
// ============================================================

export const getMemberById = async (id) => {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    throw new Error("User not authenticated");
  }

  const docRef = doc(db, "gyms", uid, "members", id);

  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    throw new Error("Member not found");
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
};

// ============================================================
// UPDATE MEMBER
// ============================================================

export const updateMember = async (id, data) => {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    throw new Error("User not authenticated");
  }

  const docRef = doc(db, "gyms", uid, "members", id);

  await updateDoc(docRef, data);
};

// ============================================================
// EXPIRY NOTIFICATIONS
// ============================================================

export const checkMemberExpiryNotifications = async () => {
  const uid = auth.currentUser?.uid;

  if (!uid) {
    throw new Error("User not authenticated");
  }

  const snapshot = await getDocs(collection(db, "gyms", uid, "members"));

  const members = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  const today = new Date();

  for (const member of members) {
    if (!member.expiryDate) continue;

    const expiry = new Date(member.expiryDate);

    const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if ([7, 3, 1, 0].includes(daysLeft)) {
      const q = query(
        collection(db, "gyms", uid, "memberNotifications"),
        where("memberId", "==", member.id),
        where("expiryDate", "==", member.expiryDate),
        where("daysLeft", "==", daysLeft),
      );

      const existing = await getDocs(q);

      if (existing.empty) {
        await addDoc(collection(db, "gyms", uid, "memberNotifications"), {
          memberId: member.id,

          fullName: member.fullName,

          phone: member.phone,

          expiryDate: member.expiryDate,

          daysLeft,

          message: `${member.fullName} membership expires in ${daysLeft} day${
            daysLeft > 1 ? "s" : ""
          }`,

          read: false,

          createdAt: new Date(),
        });
      }
    }
  }

  console.log("checkMemberExpiryNotifications called");
};

// import {
//   collection,
//   addDoc,
//   getDocs,
//   deleteDoc,
//   doc,
//   updateDoc,
//   getDoc,
//   setDoc,
// } from "firebase/firestore";

// import { db, auth } from "../firebase/config";
// import { query, where } from "firebase/firestore";
// import { storage } from "../firebase/config";
// import { ref, deleteObject } from "firebase/storage";
// import { increment } from "firebase/firestore";

// export const getMemberCount = async () => {
//   const snapshot = await getDocs(
//     collection(db, "gyms", auth.currentUser.uid, "members"),
//   );

//   return snapshot.size;
// };

// const getMemberCollection = () => {
//   const uid = auth.currentUser?.uid;

//   if (!uid) {
//     throw new Error("User not authenticated");
//   }

//   return collection(db, "gyms", uid, "members");
// };

// // export const addMember = async (data) => {
// //   const memberRef = await addDoc(getMemberCollection(), data);
// //   if (data.imageSize > 0) {
// //     await updateDoc(doc(db, "gyms", auth.currentUser.uid), {
// //       storageUsed: increment(Number(data.imageSize)),
// //     });
// //   }

// //   await updateDoc(memberRef, {
// //     memberId: `MEM-${memberRef.id.slice(0, 6).toUpperCase()}`,
// //   });

// //   // Create Portal Mapping
// //   await setDoc(doc(db, "memberPortal", data.accessToken), {
// //     gymId: auth.currentUser.uid,
// //     memberId: memberRef.id,
// //     createdAt: new Date().toISOString(),
// //   });

// //   return memberRef.id;
// // };

// // export const addMember = async (data) => {
// //   // Create member first
// //   const memberRef = await addDoc(getMemberCollection(), data);

// //   // Update storage
// //   if (data.imageSize > 0) {
// //     await updateDoc(doc(db, "gyms", auth.currentUser.uid), {
// //       storageUsed: increment(Number(data.imageSize)),
// //     });
// //   }

// //   // Generate a new portal document reference
// //   const portalRef = doc(collection(db, "memberPortal"));

// //   // Save portal mapping
// //   await setDoc(portalRef, {
// //     gymId: auth.currentUser.uid,
// //     memberId: memberRef.id,
// //     createdAt: new Date().toISOString(),
// //   });

// //   // Save memberId and portal token back into the member
// //   await updateDoc(memberRef, {
// //     memberId: `MEM-${memberRef.id.slice(0, 6).toUpperCase()}`,
// //     accessToken: portalRef.id,
// //   });

// //   return memberRef.id;
// // };

// export const addMember = async (data) => {
//   // Create member using the data received from AddMember.jsx
//   // This already contains the generated memberId such as MEM-000005
//   const memberRef = await addDoc(getMemberCollection(), data);

//   // Update storage usage
//   if (data.imageSize > 0) {
//     await updateDoc(doc(db, "gyms", auth.currentUser.uid), {
//       storageUsed: increment(Number(data.imageSize)),
//     });
//   }

//   // Create portal document
//   const portalRef = doc(collection(db, "memberPortal"));

//   await setDoc(portalRef, {
//     gymId: auth.currentUser.uid,

//     // Keep Firestore document ID here because your portal
//     // and internal member references can use this ID
//     memberId: memberRef.id,

//     createdAt: new Date().toISOString(),
//   });

//   // ONLY update accessToken.
//   // DO NOT overwrite memberId.
//   await updateDoc(memberRef, {
//     accessToken: portalRef.id,
//   });

//   return memberRef.id;
// };

// // export const getMembers = async () => {
// //   const snapshot = await getDocs(getMemberCollection());

// //   return snapshot.docs.map((doc) => ({
// //     id: doc.id,
// //     ...doc.data(),
// //   }));
// // };

// export const getMembers = async () => {
//   const snapshot = await getDocs(getMemberCollection());

//   const members = snapshot.docs.map((doc) => ({
//     id: doc.id,
//     ...doc.data(),
//   }));

//   console.log("MEMBERS FROM FIRESTORE:", members);

//   return members;
// };

// export const deleteMember = async (id) => {
//   const uid = auth.currentUser?.uid;

//   if (!uid) {
//     throw new Error("User not authenticated");
//   }

//   // Member document
//   const memberRef = doc(db, "gyms", uid, "members", id);

//   const memberSnap = await getDoc(memberRef);

//   if (!memberSnap.exists()) {
//     throw new Error("Member not found");
//   }

//   const member = memberSnap.data();

//   // Delete photo from Firebase Storage
//   if (member.photoPath) {
//     console.log("Logged in UID:", auth.currentUser.uid);
//     console.log("Photo Path:", member.photoPath);

//     try {
//       await deleteObject(ref(storage, member.photoPath));

//       console.log("Photo deleted successfully");
//     } catch (error) {
//       console.error("Delete Error:", error);
//       console.error("Error Code:", error.code);
//       console.error("Error Message:", error.message);

//       throw error;
//     }
//   }

//   // Decrease storage used
//   if (member.imageSize > 0) {
//     await updateDoc(doc(db, "gyms", uid), {
//       storageUsed: increment(-Number(member.imageSize)),
//     });
//   }

//   // Delete member document
//   await deleteDoc(memberRef);

//   // Delete portal mapping
//   if (member.accessToken) {
//     try {
//       await deleteDoc(doc(db, "memberPortal", member.accessToken));
//     } catch (error) {
//       console.log(error);
//     }
//   }
// };

// export const getMemberById = async (id) => {
//   const uid = auth.currentUser?.uid;

//   if (!uid) {
//     throw new Error("User not authenticated");
//   }

//   const docRef = doc(db, "gyms", uid, "members", id);

//   const snapshot = await getDoc(docRef);

//   if (!snapshot.exists()) {
//     throw new Error("Member not found");
//   }

//   return {
//     id: snapshot.id,
//     ...snapshot.data(),
//   };
// };

// export const updateMember = async (id, data) => {
//   const uid = auth.currentUser?.uid;

//   if (!uid) {
//     throw new Error("User not authenticated");
//   }

//   const docRef = doc(db, "gyms", uid, "members", id);

//   await updateDoc(docRef, data);
// };

// export const checkMemberExpiryNotifications = async () => {
//   const snapshot = await getDocs(
//     collection(db, "gyms", auth.currentUser.uid, "members"),
//   );

//   const members = snapshot.docs.map((doc) => ({
//     id: doc.id,
//     ...doc.data(),
//   }));

//   const today = new Date();

//   for (const member of members) {
//     if (!member.expiryDate) continue;

//     const expiry = new Date(member.expiryDate);

//     const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

//     if ([7, 3, 1, 0].includes(daysLeft)) {
//       const q = query(
//         collection(db, "gyms", auth.currentUser.uid, "memberNotifications"),
//         where("memberId", "==", member.id),
//         where("expiryDate", "==", member.expiryDate),
//         where("daysLeft", "==", daysLeft),
//       );

//       const existing = await getDocs(q);

//       if (existing.empty) {
//         await addDoc(
//           collection(db, "gyms", auth.currentUser.uid, "memberNotifications"),
//           {
//             memberId: member.id,
//             fullName: member.fullName,
//             phone: member.phone,
//             expiryDate: member.expiryDate,
//             daysLeft,
//             message: `${member.fullName} membership expires in ${daysLeft} day${
//               daysLeft > 1 ? "s" : ""
//             }`,
//             read: false,
//             createdAt: new Date(),
//           },
//         );
//       }
//     }
//   }
//   console.log("checkMemberExpiryNotifications called");
// };
